// src/components/common/SearchFilter.js
import { useSearch } from "../../context/SearchContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { useProducts } from "../../context/ProductContext";
import { productDetailPathSegment } from "../../utils/productVariants";

const SearchFilter = ({ maxWidthClass = "max-w-md", wrapperClassName = "px-2" }) => {
  const { searchQuery, setSearchQuery } = useSearch();
  const { allProducts } = useProducts();
  const [suggestions, setSuggestions] = useState([]);
  const [inputPosition, setInputPosition] = useState(null);
  const inputRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchQuery.trim()) {
        const matches = allProducts.filter((p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setSuggestions(matches.slice(0, 5));
      } else {
        setSuggestions([]);
      }

      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setInputPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [searchQuery, allProducts]);

  useEffect(() => {
    setSuggestions([]);
  }, [location.pathname]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (item) => {
    navigate(`/products/${productDetailPathSegment(item)}`, {
      state: { product: item },
    });
    setSuggestions([]);
  };

  return (
    <>
      <div
        ref={inputRef}
        className={`relative w-full ${maxWidthClass} ${wrapperClassName}`}
      >
        <form
          onSubmit={handleSubmit}
          className="flex items-center rounded-full border border-stone-200/90 bg-stone-50/80 px-4 py-2 shadow-sm transition-all focus-within:border-orange-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-orange-400/40"
        >
          <FiSearch className="mr-2 text-lg text-orange-500/80" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search..."
            className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {/* Fixed slot so mounting the clear control doesn’t shift layout / steal focus */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-500"
                title="Clear"
                tabIndex={-1}
              >
                <FiX className="text-lg" />
              </button>
            ) : (
              <span className="block h-5 w-5 shrink-0" aria-hidden />
            )}
          </div>
          <button type="submit" className="sr-only" tabIndex={-1}>
            Search
          </button>
        </form>
      </div>

      {suggestions.length > 0 && inputPosition && (
        <ul
          className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg text-sm"
          style={{
            top: `${inputPosition.top}px`,
            left: `${inputPosition.left}px`,
            width: `${inputPosition.width}px`,
          }}
        >
          {suggestions.map((item) => (
            <li
              key={item._id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              onMouseDown={() => handleSuggestionClick(item)}
            >
              {item.name}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default SearchFilter;
